import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as svc from "../../api/financeService";
import { getApiErrorMessage } from "../../utils/apiError";

// Expenses
export const fetchExpenses = createAsyncThunk("finance/fetchExpenses", async (params) => {
  const res = await svc.getExpenses(params);
  return res.data;
});
export const createExpenseThunk = createAsyncThunk("finance/createExpense", async (data, { rejectWithValue }) => {
  try {
    const res = await svc.createExpense(data);
    return res.data;
  } catch (err) {
    return rejectWithValue(getApiErrorMessage(err, "Failed to save expense."));
  }
});
export const updateExpenseThunk = createAsyncThunk("finance/updateExpense", async ({ id, data }, { rejectWithValue }) => {
  try {
    const res = await svc.updateExpense(id, data);
    return res.data;
  } catch (err) {
    return rejectWithValue(getApiErrorMessage(err, "Failed to save expense."));
  }
});
export const deleteExpenseThunk = createAsyncThunk("finance/deleteExpense", async (id, { rejectWithValue }) => {
  try {
    await svc.deleteExpense(id);
    return id;
  } catch (err) {
    return rejectWithValue(getApiErrorMessage(err, "Failed to delete expense."));
  }
});

// Income
export const fetchIncomeEntries = createAsyncThunk("finance/fetchIncome", async (params) => {
  const res = await svc.getIncomeEntries(params);
  return res.data;
});
export const createIncomeEntryThunk = createAsyncThunk("finance/createIncome", async (data, { rejectWithValue }) => {
  try {
    const res = await svc.createIncomeEntry(data);
    return res.data;
  } catch (err) {
    return rejectWithValue(getApiErrorMessage(err, "Failed to save income entry."));
  }
});
export const updateIncomeEntryThunk = createAsyncThunk("finance/updateIncome", async ({ id, data }, { rejectWithValue }) => {
  try {
    const res = await svc.updateIncomeEntry(id, data);
    return res.data;
  } catch (err) {
    return rejectWithValue(getApiErrorMessage(err, "Failed to save income entry."));
  }
});
export const deleteIncomeEntryThunk = createAsyncThunk("finance/deleteIncome", async (id, { rejectWithValue }) => {
  try {
    await svc.deleteIncomeEntry(id);
    return id;
  } catch (err) {
    return rejectWithValue(getApiErrorMessage(err, "Failed to delete income entry."));
  }
});

// Journal
export const fetchJournalEntries = createAsyncThunk("finance/fetchJournal", async (params) => {
  const res = await svc.getJournalEntries(params);
  return res.data;
});
export const createJournalEntryThunk = createAsyncThunk("finance/createJournal", async (data, { rejectWithValue }) => {
  try {
    const res = await svc.createJournalEntry(data);
    return res.data;
  } catch (err) {
    return rejectWithValue(getApiErrorMessage(err, "Failed to save journal entry."));
  }
});
export const updateJournalEntryThunk = createAsyncThunk("finance/updateJournal", async ({ id, data }, { rejectWithValue }) => {
  try {
    const res = await svc.updateJournalEntry(id, data);
    return res.data;
  } catch (err) {
    return rejectWithValue(getApiErrorMessage(err, "Failed to save journal entry."));
  }
});
export const deleteJournalEntryThunk = createAsyncThunk("finance/deleteJournal", async (id, { rejectWithValue }) => {
  try {
    await svc.deleteJournalEntry(id);
    return id;
  } catch (err) {
    return rejectWithValue(getApiErrorMessage(err, "Failed to delete journal entry."));
  }
});

// Supplier Advance
export const fetchSupplierAdvances = createAsyncThunk("finance/fetchAdvances", async (params) => {
  const res = await svc.getSupplierAdvances(params);
  return res.data;
});
export const createSupplierAdvanceThunk = createAsyncThunk("finance/createAdvance", async (data, { rejectWithValue }) => {
  try {
    const res = await svc.createSupplierAdvance(data);
    return res.data;
  } catch (err) {
    return rejectWithValue(getApiErrorMessage(err, "Failed to save supplier advance."));
  }
});
export const updateSupplierAdvanceThunk = createAsyncThunk("finance/updateAdvance", async ({ id, data }, { rejectWithValue }) => {
  try {
    const res = await svc.updateSupplierAdvance(id, data);
    return res.data;
  } catch (err) {
    return rejectWithValue(getApiErrorMessage(err, "Failed to save supplier advance."));
  }
});
export const deleteSupplierAdvanceThunk = createAsyncThunk("finance/deleteAdvance", async (id, { rejectWithValue }) => {
  try {
    await svc.deleteSupplierAdvance(id);
    return id;
  } catch (err) {
    return rejectWithValue(getApiErrorMessage(err, "Failed to delete supplier advance."));
  }
});

// Supplier Payments
export const fetchSupplierPayments = createAsyncThunk("finance/fetchPayments", async (params) => {
  const res = await svc.getSupplierPayments(params);
  return res.data;
});
export const createSupplierPaymentThunk = createAsyncThunk("finance/createPayment", async (data, { rejectWithValue }) => {
  try {
    const res = await svc.createSupplierPayment(data);
    return res.data;
  } catch (err) {
    return rejectWithValue(getApiErrorMessage(err, "Failed to record payment."));
  }
});
export const updateSupplierPaymentThunk = createAsyncThunk("finance/updatePayment", async ({ id, data }, { rejectWithValue }) => {
  try {
    const res = await svc.updateSupplierPayment(id, data);
    return res.data;
  } catch (err) {
    return rejectWithValue(getApiErrorMessage(err, "Failed to record payment."));
  }
});

const financeSlice = createSlice({
  name: "finance",
  initialState: {
    expenses: [],
    income: [],
    journal: [],
    advances: [],
    payments: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Expenses
      .addCase(fetchExpenses.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchExpenses.fulfilled, (state, action) => { state.loading = false; state.expenses = Array.isArray(action.payload) ? action.payload : []; })
      .addCase(fetchExpenses.rejected, (state, action) => { state.loading = false; state.error = action.error.message; })
      .addCase(deleteExpenseThunk.fulfilled, (state, action) => {
        state.expenses = state.expenses.filter((e) => (e.expenseID ?? e.id) !== action.payload);
      })
      // Income
      .addCase(fetchIncomeEntries.pending, (state) => { state.loading = true; })
      .addCase(fetchIncomeEntries.fulfilled, (state, action) => { state.loading = false; state.income = Array.isArray(action.payload) ? action.payload : []; })
      .addCase(fetchIncomeEntries.rejected, (state) => { state.loading = false; })
      .addCase(deleteIncomeEntryThunk.fulfilled, (state, action) => {
        state.income = state.income.filter((e) => (e.incomeID ?? e.id) !== action.payload);
      })
      // Journal
      .addCase(fetchJournalEntries.pending, (state) => { state.loading = true; })
      .addCase(fetchJournalEntries.fulfilled, (state, action) => { state.loading = false; state.journal = Array.isArray(action.payload) ? action.payload : []; })
      .addCase(fetchJournalEntries.rejected, (state) => { state.loading = false; })
      .addCase(deleteJournalEntryThunk.fulfilled, (state, action) => {
        state.journal = state.journal.filter((e) => (e.journalID ?? e.id) !== action.payload);
      })
      // Advances
      .addCase(fetchSupplierAdvances.pending, (state) => { state.loading = true; })
      .addCase(fetchSupplierAdvances.fulfilled, (state, action) => { state.loading = false; state.advances = Array.isArray(action.payload) ? action.payload : []; })
      .addCase(fetchSupplierAdvances.rejected, (state) => { state.loading = false; })
      .addCase(deleteSupplierAdvanceThunk.fulfilled, (state, action) => {
        state.advances = state.advances.filter((e) => (e.advanceID ?? e.id) !== action.payload);
      })
      // Payments
      .addCase(fetchSupplierPayments.pending, (state) => { state.loading = true; })
      .addCase(fetchSupplierPayments.fulfilled, (state, action) => { state.loading = false; state.payments = Array.isArray(action.payload) ? action.payload : []; })
      .addCase(fetchSupplierPayments.rejected, (state) => { state.loading = false; });
  },
});

export default financeSlice.reducer;
